import { PlusIcon, TagIcon } from "@phosphor-icons/react";
import { isValidElement, useState } from "react";
import { DialogTrigger } from "react-aria-components";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/controls/command";
import { Skeleton } from "@/components/ui/feedback/skeleton";
import Checkbox from "@/components/ui/forms/checkbox";
import { Popover } from "@/components/ui/popover";
import { toast } from "@/components/ui/toast";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface LabelOption<TId extends string | number = string | number> {
  id: TId;
  name: string;
}

interface LabelComboboxProps<TId extends string | number = string | number> {
  labels: LabelOption<TId>[];
  selectedLabels: LabelOption<TId>[];
  onSelectedLabelsChange: (labels: LabelOption<TId>[]) => void;
  /** Creates a tag; the returned value is added to the selection. Return `null` when the failure was already surfaced. */
  onCreateLabel: (
    name: string,
  ) => Promise<LabelOption<TId> | null> | LabelOption<TId> | null;
  isLoading?: boolean;
  placeholder?: string;
}

interface LabelComboboxTriggerProps {
  children: React.ReactNode;
  label: string;
  icon?: React.ReactNode;
  className?: string;
  variant?: "default" | "icon-only";
}

const commandItemStyles =
  "px-3 py-2 text-gray-700 data-[selected=true]:font-normal aria-selected:font-normal";

const LabelCombobox = <TId extends string | number = string | number>({
  labels,
  selectedLabels,
  onSelectedLabelsChange,
  onCreateLabel,
  isLoading = false,
  placeholder,
}: LabelComboboxProps<TId>) => {
  const [query, setQuery] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const labelsById = new Map(selectedLabels.map((label) => [label.id, label]));
  for (const label of labels) {
    labelsById.set(label.id, label);
  }
  const knownLabels = [...labelsById.values()];

  const trimmedQuery = query.trim();
  const filteredLabels = knownLabels.filter((label) =>
    label.name.toLowerCase().includes(trimmedQuery.toLowerCase()),
  );
  const selectedIds = new Set(selectedLabels.map((label) => label.id));
  const selectedIdOrder = new Map(
    selectedLabels.map((label, index) => [label.id, index]),
  );
  const selectedMatches = filteredLabels
    .filter((label) => selectedIds.has(label.id))
    .sort(
      (a, b) =>
        (selectedIdOrder.get(b.id) ?? 0) - (selectedIdOrder.get(a.id) ?? 0),
    );
  const unselectedMatches = filteredLabels.filter(
    (label) => !selectedIds.has(label.id),
  );
  const hasBoundary =
    selectedMatches.length > 0 && unselectedMatches.length > 0;

  const hasExactMatch = knownLabels.some(
    (label) => label.name.toLowerCase() === trimmedQuery.toLowerCase(),
  );
  const showCreate = trimmedQuery !== "" && !hasExactMatch;

  const toggleLabel = (label: LabelOption<TId>) => {
    if (selectedIds.has(label.id)) {
      onSelectedLabelsChange(
        selectedLabels.filter((selected) => selected.id !== label.id),
      );
      return;
    }
    onSelectedLabelsChange([...selectedLabels, label]);
  };

  const renderLabelRow = (label: LabelOption<TId>) => {
    const isSelected = selectedIds.has(label.id);
    return (
      <CommandItem
        key={label.id}
        value={label.name}
        aria-label={`${label.name}, ${isSelected ? "selected" : "not selected"}`}
        className={commandItemStyles}
        onSelect={() => toggleLabel(label)}
      >
        <Checkbox
          iconClassNames="size-[11px]"
          className="mr-1.5 pointer-events-none"
          tabIndex={-1}
          aria-hidden
          checked={isSelected}
        />
        {label.name}
      </CommandItem>
    );
  };

  const handleCreate = async () => {
    if (!trimmedQuery || isCreating || isLoading) {
      return;
    }
    setIsCreating(true);
    try {
      const created = await onCreateLabel(trimmedQuery);
      if (!created) {
        return;
      }
      onSelectedLabelsChange(
        selectedIds.has(created.id)
          ? selectedLabels.map((label) =>
              label.id === created.id ? created : label,
            )
          : [...selectedLabels, created],
      );
      setQuery("");
    } catch {
      toast.error("Failed to create tag");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Command shouldFilter={false}>
      <CommandInput
        value={query}
        onValueChange={setQuery}
        placeholder={placeholder}
        icon={null}
        disabled={isLoading}
        className="font-normal text-xs text-gray-700 placeholder:text-gray-400"
        wrapperClassName="px-3 focus-within:border-transparent focus-within:border-b-border"
      />
      <CommandList className="pt-1 pb-2 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent hover:scrollbar-thumb-slate-300">
        {isLoading ? (
          [0, 1].map((index) => (
            <CommandItem
              key={`loading-skeleton-${index}`}
              value={`loading-skeleton-${index}`}
              disabled
              className={commandItemStyles}
            >
              <Skeleton className="size-4 shrink-0 rounded-xs bg-slate-200" />
              <Skeleton
                className={cn(
                  "h-4 rounded-sm bg-slate-200",
                  index === 0 ? "w-28" : "w-20",
                )}
              />
            </CommandItem>
          ))
        ) : (
          <>
            <CommandEmpty className="py-2 px-3 text-left text-xs text-gray-400">
              Type to create a new tag
            </CommandEmpty>

            {showCreate ? (
              <CommandItem
                value={`create-${trimmedQuery}`}
                className={commandItemStyles}
                disabled={isCreating}
                onSelect={handleCreate}
              >
                <PlusIcon className="size-4" />
                <span className="text-gray-700">Create new tag:</span>
                <span className="text-gray-600">"{trimmedQuery}"</span>
              </CommandItem>
            ) : null}

            {selectedMatches.map(renderLabelRow)}

            {hasBoundary && (
              <CommandSeparator alwaysRender className="mx-0 my-2" />
            )}

            {unselectedMatches.map(renderLabelRow)}
          </>
        )}
      </CommandList>
    </Command>
  );
};

const LabelComboboxTrigger = ({
  children,
  label,
  icon,
  className,
  variant = "default",
}: LabelComboboxTriggerProps) => {
  const isIconOnly = variant === "icon-only";

  return (
    <DialogTrigger>
      <TooltipTrigger>
        <Button
          variant={isIconOnly ? "ghost" : "outline"}
          aria-label={isIconOnly ? label : undefined}
          className={cn(
            isIconOnly
              ? "inline-flex size-6 shrink-0 items-center justify-center p-1.5 text-slate-800 hover:bg-slate-100 active:bg-slate-200 bg-transparent"
              : "text-xs text-gray-700 rounded-2xl px-2.5 h-6.5 font-normal",
            className,
          )}
        >
          {isIconOnly ? (
            isValidElement(icon) ? (
              icon
            ) : (
              <PlusIcon className="size-4" />
            )
          ) : (
            <>
              {icon ?? <TagIcon className="size-3.5 mr-2 text-gray-600" />}
              {label}
            </>
          )}
        </Button>
        {isIconOnly ? <Tooltip placement="bottom">{label}</Tooltip> : null}
      </TooltipTrigger>
      <Popover
        placement="bottom start"
        className="w-75 bg-white border border-gray-200 rounded-md shadow-xl p-0 overflow-hidden"
      >
        {children}
      </Popover>
    </DialogTrigger>
  );
};

export default LabelCombobox;
export type { LabelOption };
export { LabelComboboxTrigger };
